/**
 * @autogenerated
 *
 * WARNING: This file is automatically generated based on Contentful schema.
 * DO NOT modify this file manually - your changes will be overwritten.
 *
 * To regenerate this file, run: `yarn contentful generate`.
 * To customize the generated code:
 *   - Modify the schema in Contentful and regenerate.
 *   - Override this module in higher-level packages.
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseJobListingById, getBaseJobListings, getFullJobListingById, getFullJobListings, getJobListingById, getJobListings,
} from '@amblique/contentful/app/contentful/queries';

/**
 * Hook for fetching a JobListing by its ID
 *
 * @param {string} id - The Contentful entry ID
 * @param {Object} [options={}] - Hook options
 * @param {'base'|'default'|'full'} [options.variant='default'] - Query variant (base: scalars only, default: 1-level deep, full: 2-levels deep)
 * @param {Omit<Contentful.QueryJobListingArgs, 'id'>} [options.variables={}] - GraphQL query variables
 * @param {import('@tanstack/react-query').UseQueryOptions<Contentful.JobListing|null, Error>} [options.queryOptions={}] - React Query options
 * @param {RequestInit} [options.requestOptions={}] - HTTP request options (headers, cache, etc.)
 * @returns {import('@tanstack/react-query').UseQueryResult<Contentful.JobListing|null, Error>}
 *
 * @example
 * // Basic usage (default variant)
 * const { data: jobListing, isLoading } = useJobListingById('entry-id');
 *
 * @example
 * // With variant selection
 * const { data } = useJobListingById('entry-id', {
 *   variant: 'full',  // Get complete nested data
 *   variables: { preview: true }
 * });
 *
 * @example
 * // With all options
 * const { data } = useJobListingById('entry-id', {
 *   variant: 'base',  // Fast, scalars only
 *   variables: { preview: true },
 *   queryOptions: { staleTime: 0 },
 *   requestOptions: { cache: 'no-store' }
 * });
 */
export function useJobListingById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'default',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  // Select query based on variant
  const query = variant === 'base' ?
    getBaseJobListingById :
    variant === 'full' ?
      getFullJobListingById :
      getJobListingById;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'jobListing',
      'id',
      id,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          id,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.jobListing || null;
    },

    enabled: !!contentfulService && !!id,

    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,

    ...queryOptions,
  });
}

/**
 * Hook for fetching all JobListing entries with pagination
 *
 * @param {Object} [options={}] - Hook options
 * @param {'base'|'default'|'full'} [options.variant='default'] - Query variant (base: scalars only, default: 1-level deep, full: 2-levels deep)
 * @param {Contentful.QueryJobListingCollectionArgs} [options.variables={}] - GraphQL query variables (limit, skip, order, where, etc.)
 * @param {import('@tanstack/react-query').UseQueryOptions<Contentful.JobListingCollection, Error>} [options.queryOptions={}] - React Query options
 * @param {RequestInit} [options.requestOptions={}] - HTTP request options (headers, cache, etc.)
 * @returns {import('@tanstack/react-query').UseQueryResult<Contentful.JobListingCollection, Error>}
 *
 * @example
 * // Basic usage (default variant)
 * const { data: collection, isLoading } = useJobListings();
 *
 * @example
 * // Fast list view with base variant
 * const { data: collection } = useJobListings({
 *   variant: 'base',  // Scalars only, fast!
 *   variables: { limit: 20 }
 * });
 *
 * @example
 * // With pagination and filters
 * const { data: collection } = useJobListings({
 *   variant: 'default',
 *   variables: {
 *     limit: 20,
 *     skip: 0,
 *     order: ['sys_publishedAt_DESC'],
 *     where: { title: 'Example' },
 *     preview: true
 *   }
 * });
 *
 * @example
 * // Full nested data for detail views
 * const { data: collection } = useJobListings({
 *   variant: 'full',  // Complete nested data
 *   variables: { limit: 10 }
 * });
 */
export function useJobListings(options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'default',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  // Select query based on variant
  const query = variant === 'base' ?
    getBaseJobListings :
    variant === 'full' ?
      getFullJobListings :
      getJobListings;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'jobListing',
      'collection',
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          limit: 10,
          skip: 0,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.jobListingCollection || {total: 0, skip: 0, limit: 0, items: []};
    },

    enabled: !!contentfulService,

    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,

    retry: (failureCount, error) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }

      return failureCount < 3;
    },

    ...queryOptions,
  });
}
